/**
 * Auth provider factory (task_108).
 *
 * Reads `VITE_AUTH_PROVIDER` at module-load time and returns the matching
 * provider singleton. No per-request switching — change requires rebuild.
 * The chosen provider is logged on first resolution so the dev console
 * visibly confirms which path is live.
 */

import type { AuthProvider } from './AuthProvider';
import { FirebaseAuthProvider } from './FirebaseAuthProvider';
import { StudioAuthProvider } from './StudioAuthProvider';

export type AuthProviderName = 'firebase' | 'studio_sso';

let cached: AuthProvider | null = null;

const resolveProviderName = (): AuthProviderName => {
  const raw = (import.meta.env.VITE_AUTH_PROVIDER ?? '').toLowerCase();
  if (raw === '') {
    return 'firebase';
  }
  if (raw !== 'firebase' && raw !== 'studio_sso') {
    throw new Error(
      `Unknown VITE_AUTH_PROVIDER: ${raw}. Expected 'firebase' or 'studio_sso'.`
    );
  }
  return raw as AuthProviderName;
};

export const getAuthProvider = (): AuthProvider => {
  if (cached) {
    return cached;
  }

  const name = resolveProviderName();
  cached = name === 'firebase' ? new FirebaseAuthProvider() : new StudioAuthProvider();

  console.info(
    `🔐 [AUTH] Auth provider selected: ${name} (${cached.constructor.name})`
  );

  return cached;
};

/** Test-only: clear the cached provider so a different env value takes effect. */
export const resetAuthProviderCache = (): void => {
  cached = null;
};
